import React, { useState, useRef, useEffect, useContext } from "react";
import {
  Row,
  Col,
  Dropdown,
  Table,
  Button,
  Modal,
  Badge,
  Form,
} from "react-bootstrap";
import {
  Eye,
  CheckCircle,
  ChatDots,
  Send,
  XCircle,
} from "react-bootstrap-icons";
import AdminMasterLayout from "../../../layouts/Admin/AdminMasterLayout";
import "../../../assets/css/Admin/HelpSupport/HelpSupport.css";
import axios from "axios";
import AppContext from "../../../context/AppContext";

const HelpSupport = () => {
  const { baseUrl, authToken } = useContext(AppContext);
  const [selectedUserType, setSelectedUserType] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const chatEndRef = useRef(null);
  const pollingRef = useRef(null);
  const [conversationId, setConversationId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [chats, setChats] = useState({});
  const prevMessageCount = useRef(0);

  const selectedTicket = tickets.find((t) => t.id === selectedTicketId) || null;

  const filteredTickets =
    selectedUserType === "All"
      ? tickets
      : tickets.filter((t) => t.raisedby === selectedUserType.toLowerCase());

  useEffect(() => {
    axios
      .post(
        `${baseUrl}/admin/helpsupport/viewTickets`,
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      )
      .then((res) => {
        if (res.data.success === true) {
          setTickets(res.data.tickets);
          console.log("All tickets:", res.data);
          setLoading(false);
        }
      })
      .catch((error) => {
        console.log(error);
        alert("Network Error!");
      });
  }, []);

  const fetchMessages = (ticket) => {
    const formData = new FormData();
    formData.append("ticket_id", ticket.id);
    axios
      .post(`${baseUrl}/admin/helpsupport/getMessages`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        console.log("get message:", res.data);

        if (res.data.success === true && res.data.conversation_id) {
          setConversationId(res.data.conversation_id);
          setChats((prev) => ({
            ...prev,
            [ticket.id]: res.data.messages || [],
          }));
        }
      })
      .catch((error) => console.log(error));
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicketId(ticket.id);
    setReplyText("");
    setShowModal(true);
    fetchMessages(ticket);
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(() => fetchMessages(ticket), 3000);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };
  const handleMarkAsClosed = (ticketId) => {
    const formData = new FormData();
    formData.append("id", ticketId);
    axios
      .post(`${baseUrl}/admin/helpsupport/closeTicket`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.success === true) {
          console.log("close ticket:", res.data);

          setTickets((prev) =>
            prev.map((t) =>
              t.id === ticketId ? { ...t, status: "Closed" } : t,
            ),
          );
          setShowModal(false);
        }
      })
      .catch((error) => {
        console.log(error);
        alert("Network Error!");
      });
  };

  const handleSendReply = () => {
    if (!replyText.trim() || !conversationId) {
      console.log("conversationId:", conversationId); // debug
      return;
    }
    const formData = new FormData();
    formData.append("tdconversation_id", conversationId);
    formData.append("message", replyText.trim());
    axios
      .post(`${baseUrl}/admin/helpsupport/sendMessage`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        console.log("send:", res.data);
        if (res.data.success === true) {
          setChats((prev) => ({
            ...prev,
            [selectedTicketId]: [
              ...(prev[selectedTicketId] || []),
              res.data.chat,
            ],
          }));
          setReplyText("");
        }
      })
      .catch((error) => {
        console.log(error);
        alert("Network Error!");
      });
  };

  useEffect(() => {
    if (!showModal) return;
    const currentMessages = chats[selectedTicketId] || [];
    if (currentMessages.length > prevMessageCount.current) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      prevMessageCount.current = currentMessages.length;
    }
  }, [chats, showModal]);

  const getPriorityBadge = (priority) => {
    const colors = {
      Critical: "danger",
      High: "warning",
      Medium: "info",
      Low: "secondary",
    };
    return (
      <Badge bg={colors[priority] || "secondary"}>{priority || "Medium"}</Badge>
    );
  };

  return (
    <AdminMasterLayout>
      <div className="help-support-container">
        <Row className="help-support-header">
          <Col>
            <h2 className="help-support-title">Help & Support</h2>
            <p className="help-support-subtitle">
              Manage customer and driver support tickets
            </p>
          </Col>
        </Row>

        <Row className="help-support-filter-row">
          <Col md={3}>
            <div className="help-support-dropdown-wrapper">
              <label className="help-support-label">Select User Type:</label>
              <Dropdown onSelect={(e) => setSelectedUserType(e)}>
                <Dropdown.Toggle
                  className="help-support-dropdown-btn"
                  id="dropdown-basic"
                >
                  {selectedUserType === "All"
                    ? "All Tickets"
                    : selectedUserType}
                </Dropdown.Toggle>
                <Dropdown.Menu className="help-support-dropdown-menu">
                  <Dropdown.Item eventKey="All">All Tickets</Dropdown.Item>
                  <Dropdown.Item eventKey="Customer">Customer</Dropdown.Item>
                  <Dropdown.Item eventKey="Driver">Driver</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </Col>
          <Col md={9} className="text-end">
            <div className="help-support-stats">
              <span className="help-support-stat-item">
                Total: <strong>{filteredTickets.length}</strong>
              </span>
              <span className="help-support-stat-item">
                Open:{" "}
                <strong>
                  {filteredTickets.filter((t) => t.status === "Open").length}
                </strong>
              </span>
              <span className="help-support-stat-item">
                Closed:{" "}
                <strong>
                  {filteredTickets.filter((t) => t.status === "Closed").length}
                </strong>
              </span>
            </div>
          </Col>
        </Row>

        <Row className="help-support-table-row">
          <Col>
            <div className="help-support-table-wrapper">
              <Table hover responsive className="help-support-table">
                <thead>
                  <tr>
                    <th>Ticket ID</th>
                    <th>Ticket Title</th>
                    <th>User Type</th>
                    <th>User Name</th>
                    <th>Priority</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        Loading...
                      </td>
                    </tr>
                  ) : filteredTickets.length === 0 ? (
                    <tr>
                      <td
                        colSpan="8"
                        className="text-center help-support-no-data"
                      >
                        No tickets found
                      </td>
                    </tr>
                  ) : (
                    filteredTickets.map((ticket) => (
                      <tr key={ticket.id}>
                        <td className="help-support-ticket-id">
                          TKT{String(ticket.id).padStart(3, "0")}
                        </td>
                        <td>{ticket.subject}</td>
                        <td>
                          <Badge
                            bg={
                              ticket.raisedby === "customer"
                                ? "primary"
                                : "success"
                            }
                          >
                            {ticket.raisedby}
                          </Badge>
                        </td>
                        <td>
                          {ticket.Tdcustomer?.name ||
                            ticket.Tddriver?.drivername ||
                            "Unknown"}
                        </td>
                        <td>{getPriorityBadge(ticket.priority)}</td>
                        <td>{ticket.created_at?.split("T")[0]}</td>
                        <td>
                          <span
                            className={`hs-status-badge ${ticket.status === "Open" ? "status-open" : "status-closed"}`}
                          >
                            {ticket.status}
                          </span>
                        </td>
                        <td>
                          <div className="help-support-action-btns">
                            <Button
                              className="help-support-view-btn"
                              size="sm"
                              onClick={() => handleViewTicket(ticket)}
                            >
                              <Eye size={14} /> View
                            </Button>
                            {ticket.status === "Open" && (
                              <Button
                                className="help-support-close-btn"
                                size="sm"
                                onClick={() => handleMarkAsClosed(ticket.id)}
                              >
                                <CheckCircle size={14} /> Close
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>

        <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
          <Modal.Header closeButton className="help-support-modal-header">
            <Modal.Title>
              <ChatDots className="me-2" />
              Ticket Details
              {selectedTicket && (
                <span
                  className={`hs-status-badge ms-2 ${selectedTicket.status === "Open" ? "status-open" : "status-closed"}`}
                  style={{ fontSize: "11px" }}
                >
                  {selectedTicket.status}
                </span>
              )}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="help-support-modal-body">
            {selectedTicket && (
              <>
                <div className="ticket-info-grid">
                  <div className="ticket-info-item">
                    <span className="ticket-info-label">Ticket ID</span>
                    <span className="ticket-info-value teal">
                      TKT{String(selectedTicket.id).padStart(3, "0")}
                    </span>
                  </div>
                  <div className="ticket-info-item">
                    <span className="ticket-info-label">Title</span>
                    <span className="ticket-info-value">
                      {selectedTicket.subject}
                    </span>
                  </div>
                  <div className="ticket-info-item">
                    <span className="ticket-info-label">User Type</span>
                    <Badge
                      bg={
                        selectedTicket.raisedby === "customer"
                          ? "primary"
                          : "success"
                      }
                    >
                      {selectedTicket.raisedby}
                    </Badge>
                  </div>
                  <div className="ticket-info-item">
                    <span className="ticket-info-label">User Name</span>
                    <span className="ticket-info-value">
                      {selectedTicket.Tdcustomer?.customerName ||
                        selectedTicket.Tddriver?.drivername ||
                        "Unknown"}
                    </span>
                  </div>
                  <div className="ticket-info-item">
                    <span className="ticket-info-label">Priority</span>
                    {getPriorityBadge(selectedTicket.priority)}
                  </div>
                  <div className="ticket-info-item">
                    <span className="ticket-info-label">Date</span>
                    <span className="ticket-info-value">
                      {selectedTicket.created_at?.split("T")[0]}
                    </span>
                  </div>
                </div>

                <div className="ticket-description-box">
                  <span className="ticket-info-label">Description</span>
                  <p>{selectedTicket.description}</p>
                </div>

                {selectedTicket.status === "Open" && (
                  <div className="ticket-chat-section">
                    <div className="ticket-chat-header">
                      <ChatDots size={16} className="me-2" />
                      Conversation
                    </div>
                    <div className="ticket-chat-messages">
                      {(chats[selectedTicket.id] || []).length === 0 ? (
                        <p className="ticket-chat-empty">
                          No messages yet. Start the conversation!
                        </p>
                      ) : (
                        (chats[selectedTicket.id] || []).map((msg, idx) => (
                          <div
                            key={idx}
                            className={`ticket-chat-bubble ${msg.sender === "admin" ? "admin-bubble" : "user-bubble"}`}
                          >
                            <span className="bubble-sender">
                              {msg.sender === "admin"
                                ? "Admin"
                                : selectedTicket.Tdcustomer?.customerName || "User"}
                            </span>
                            <p className="bubble-text">{msg.message}</p>
                            <span className="bubble-time">
                              {new Date(msg.created_at).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          </div>
                        ))
                      )}
                      <div ref={chatEndRef} />
                    </div>
                    <div className="ticket-chat-input-row">
                      <Form.Control
                        type="text"
                        placeholder="Type your reply..."
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && handleSendReply()
                        }
                        className="ticket-chat-input"
                      />
                      <Button
                        className="ticket-chat-send-btn"
                        onClick={handleSendReply}
                      >
                        <Send size={16} />
                      </Button>
                    </div>
                  </div>
                )}

                {selectedTicket.status === "Closed" && (
                  <div className="ticket-closed-notice">
                    <XCircle size={16} className="me-2" />
                    This ticket is closed.
                  </div>
                )}
              </>
            )}
          </Modal.Body>

          <Modal.Footer className="help-support-modal-footer">
            <Button
              className="help-support-modal-close-btn"
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
            {selectedTicket && selectedTicket.status === "Open" && (
              <Button
                className="help-support-modal-mark-btn"
                onClick={() => handleMarkAsClosed(selectedTicket.id)}
              >
                <CheckCircle size={16} className="me-1" />
                Mark as Closed
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </div>
    </AdminMasterLayout>
  );
};

export default HelpSupport;
